import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { DailyReportEntry } from '../../types';
import { getLocalDateString } from '../../lib/dateUtils';
import {
  X,
  BookOpen,
  Check,
  Moon,
  Users,
  Sparkles,
  Award,
  Clock,
  Send,
  Calendar,
  Layers,
  Heart,
  Share2,
} from 'lucide-react';

interface DailyReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: string;
}

export const DailyReportModal: React.FC<DailyReportModalProps> = ({
  isOpen,
  onClose,
  initialDate,
}) => {
  const { personalReports, updateDailyReport, triggerConfetti, t } = useApp();

  const todayStr = getLocalDateString();
  const [date, setDate] = useState<string>(initialDate || todayStr);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const [formData, setFormData] = useState<DailyReportEntry>({
    date: initialDate || todayStr,
    quranTilawat: 0,
    quranTafseer: 0,
    hadithCount: 0,
    salahJamat: 5,
    salahQaza: 0,
    literatureGeneral: 0,
    literatureSyllabus: 0,
    academicStudyHours: 0,
    classCount: 0,
    classAttended: 0,
    contactMember: 0,
    contactAssociate: 0,
    contactWorker: 0,
    contactSupporter: 0,
    contactFriend: 0,
    contactTalented: 0,
    contactWellWisher: 0,
    contactMukallif: 0,
    distLiterature: 0,
    distMagazine: 0,
    distStickerCard: 0,
    distGift: 0,
    dutyDawahHours: 0,
    dutyOtherHours: 0,
    socialMediaHours: 0,
    newspaperRead: false,
    exerciseDone: false,
    selfAccountability: false,
    sleepHours: 7,
    remarks: '',
  });

  useEffect(() => {
    if (initialDate) {
      setDate(initialDate);
    }
  }, [initialDate]);

  useEffect(() => {
    const existing = personalReports.find((r) => r.date === date);
    if (existing) {
      setFormData({
        ...existing,
      });
    } else {
      setFormData({
        date,
        quranTilawat: 0,
        quranTafseer: 0,
        hadithCount: 0,
        salahJamat: 5,
        salahQaza: 0,
        literatureGeneral: 0,
        literatureSyllabus: 0,
        academicStudyHours: 0,
        classCount: 0,
        classAttended: 0,
        contactMember: 0,
        contactAssociate: 0,
        contactWorker: 0,
        contactSupporter: 0,
        contactFriend: 0,
        contactTalented: 0,
        contactWellWisher: 0,
        contactMukallif: 0,
        distLiterature: 0,
        distMagazine: 0,
        distStickerCard: 0,
        distGift: 0,
        dutyDawahHours: 0,
        dutyOtherHours: 0,
        socialMediaHours: 0,
        newspaperRead: false,
        exerciseDone: false,
        selfAccountability: false,
        sleepHours: 7,
        remarks: '',
      });
    }
  }, [date, personalReports, isOpen]);

  if (!isOpen) return null;

  const handleNumChange = (field: keyof DailyReportEntry, val: string) => {
    const num = val === '' ? 0 : parseFloat(val);
    setFormData((prev) => ({
      ...prev,
      [field]: isNaN(num) ? 0 : num,
    }));
  };

  const handleCheckboxToggle = (field: 'newspaperRead' | 'exerciseDone' | 'selfAccountability') => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateDailyReport({
      ...formData,
      date,
    });
    triggerConfetti();
    onClose();
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in overflow-y-auto"
    >
      <div className="bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden my-auto animate-scale-in">
        {/* Modal Header */}
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-500/10 text-brand-500 flex items-center justify-center font-bold">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                {t.personalReport} — Daily Accountability Log
              </h2>
              <p className="text-xs text-slate-500">Record your daily spiritual, academic & organizational progress.</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Date Selector Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-brand-50/50 dark:bg-brand-950/20 border border-brand-200/50 dark:border-brand-900/40 rounded-2xl">
            <div className="flex items-center gap-2 text-xs font-bold text-brand-700 dark:text-brand-300">
              <Calendar className="w-4 h-4 text-brand-500" />
              <span>Target Report Date:</span>
            </div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="px-3.5 py-1.5 bg-white dark:bg-slate-900 border border-brand-200 dark:border-brand-800/80 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Grid Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Section 1: Quran & Hadith */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-emerald-600 dark:text-emerald-400">
                <BookOpen className="w-4 h-4" />
                <span>1. Quran & Hadith Study</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Tilawat (Verses)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quranTilawat ?? 0}
                    onChange={(e) => handleNumChange('quranTilawat', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Tafseer (Verses)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quranTafseer ?? 0}
                    onChange={(e) => handleNumChange('quranTafseer', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Hadith (Count)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.hadithCount ?? 0}
                    onChange={(e) => handleNumChange('hadithCount', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Salah / Prayers */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-blue-600 dark:text-blue-400">
                <Heart className="w-4 h-4" />
                <span>2. Salah / Daily Prayers</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Jamat (0 to 5)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={formData.salahJamat ?? 0}
                    onChange={(e) => handleNumChange('salahJamat', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Qaza / Missed (0 to 5)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={formData.salahQaza ?? 0}
                    onChange={(e) => handleNumChange('salahQaza', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Literature & Academic Study */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-purple-600 dark:text-purple-400">
                <Layers className="w-4 h-4" />
                <span>3. Literature & Academic Study</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    General (Pages)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.literatureGeneral ?? 0}
                    onChange={(e) => handleNumChange('literatureGeneral', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Syllabus (Pages)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.literatureSyllabus ?? 0}
                    onChange={(e) => handleNumChange('literatureSyllabus', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Academic (Hours)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.academicStudyHours ?? 0}
                    onChange={(e) => handleNumChange('academicStudyHours', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Classes & Sessions */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-amber-600 dark:text-amber-400">
                <Award className="w-4 h-4" />
                <span>4. Classes & Sessions</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Classes Held
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.classCount ?? 0}
                    onChange={(e) => handleNumChange('classCount', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    Classes Attended
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.classAttended ?? 0}
                    onChange={(e) => handleNumChange('classAttended', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Contacts & Dawah Outreach */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3 md:col-span-2">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-teal-600 dark:text-teal-400">
                <Users className="w-4 h-4" />
                <span>5. Contacts, Dawah & Special Outreach</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2.5">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Member</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactMember ?? 0}
                    onChange={(e) => handleNumChange('contactMember', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Associate</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactAssociate ?? 0}
                    onChange={(e) => handleNumChange('contactAssociate', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Worker</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactWorker ?? 0}
                    onChange={(e) => handleNumChange('contactWorker', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Supporter</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactSupporter ?? 0}
                    onChange={(e) => handleNumChange('contactSupporter', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Friend</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactFriend ?? 0}
                    onChange={(e) => handleNumChange('contactFriend', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Talented</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactTalented ?? 0}
                    onChange={(e) => handleNumChange('contactTalented', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">WellWisher</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactWellWisher ?? 0}
                    onChange={(e) => handleNumChange('contactWellWisher', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Contact</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.contactMukallif ?? 0}
                    onChange={(e) => handleNumChange('contactMukallif', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Distribution */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-pink-600 dark:text-pink-400">
                <Share2 className="w-4 h-4" />
                <span>6. Distribution (Dawah Materials)</span>
              </div>
              <div className="grid grid-cols-4 gap-2.5">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Books</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.distLiterature ?? 0}
                    onChange={(e) => handleNumChange('distLiterature', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Magazine</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.distMagazine ?? 0}
                    onChange={(e) => handleNumChange('distMagazine', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Stickers</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.distStickerCard ?? 0}
                    onChange={(e) => handleNumChange('distStickerCard', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Gifts</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.distGift ?? 0}
                    onChange={(e) => handleNumChange('distGift', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Section 7: Organizational Duties & Time */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-indigo-600 dark:text-indigo-400">
                <Clock className="w-4 h-4" />
                <span>7. Duties, Media & Sleep (Hours)</span>
              </div>
              <div className="grid grid-cols-4 gap-2.5">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Dawah (Hrs)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.dutyDawahHours ?? 0}
                    onChange={(e) => handleNumChange('dutyDawahHours', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Other (Hrs)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.dutyOtherHours ?? 0}
                    onChange={(e) => handleNumChange('dutyOtherHours', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Social (Hrs)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.socialMediaHours ?? 0}
                    onChange={(e) => handleNumChange('socialMediaHours', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1">Sleep (Hrs)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.sleepHours ?? 7}
                    onChange={(e) => handleNumChange('sleepHours', e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center text-slate-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Section 8: Daily Habit Checkboxes */}
            <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800/80 rounded-2xl space-y-3 md:col-span-2">
              <div className="flex items-center gap-2 text-xs font-black uppercase text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-brand-500" />
                <span>8. Daily Checkboxes & Self-Accountability</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => handleCheckboxToggle('newspaperRead')}
                  className={`p-3.5 rounded-2xl border text-xs font-bold flex items-center justify-between transition-all ${
                    formData.newspaperRead
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    <span>Newspaper Reading</span>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-lg flex items-center justify-center border ${
                      formData.newspaperRead
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    {formData.newspaperRead && <Check className="w-3.5 h-3.5" />}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleCheckboxToggle('exerciseDone')}
                  className={`p-3.5 rounded-2xl border text-xs font-bold flex items-center justify-between transition-all ${
                    formData.exerciseDone
                      ? 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-400'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>Physical Exercise</span>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-lg flex items-center justify-center border ${
                      formData.exerciseDone
                        ? 'bg-rose-500 border-rose-500 text-white'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    {formData.exerciseDone && <Check className="w-3.5 h-3.5" />}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleCheckboxToggle('selfAccountability')}
                  className={`p-3.5 rounded-2xl border text-xs font-bold flex items-center justify-between transition-all ${
                    formData.selfAccountability
                      ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600 dark:text-indigo-400'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4" />
                    <span>Muhasabah / Self-Accountability</span>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-lg flex items-center justify-center border ${
                      formData.selfAccountability
                        ? 'bg-indigo-500 border-indigo-500 text-white'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    {formData.selfAccountability && <Check className="w-3.5 h-3.5" />}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3 bg-slate-50/50 dark:bg-slate-900/40">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-xs font-bold shadow-md shadow-brand-500/25 flex items-center gap-2 transition-all transform active:scale-95"
          >
            <Send className="w-3.5 h-3.5" />
            <span>Save Entry</span>
          </button>
        </div>
      </div>
    </div>
  );
};
